import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Zap, Lock, User, ArrowRight, Loader2, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcceptInvite = () => {
  const [params] = useSearchParams();
  const inviteToken = params.get('token');
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { acceptInvite } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!inviteToken) { setError('No invite token provided.'); setLoading(false); return; }
    axios.get(`${API}/public/team/invite/${inviteToken}`)
      .then(res => { setInvite(res.data); setName(res.data.name || ''); })
      .catch(err => setError(err.response?.data?.detail || 'This invite is invalid or expired.'))
      .finally(() => setLoading(false));
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await acceptInvite(inviteToken, password, name);
      toast.success('Welcome to the team!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept invite');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-8 animate-fade-in" data-testid="accept-invite-page">
        <div className="flex items-center gap-3 mb-8">
          <img src="/brand/ctc-favicon-64.png" alt="Care Tech Compare" className="w-11 h-11 object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Care Tech Compare</h1>
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">Team invitation</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-blue-600" /></div>
        ) : error ? (
          <div className="text-center py-8" data-testid="invite-error">
            <p className="text-gray-700 mb-4">{error}</p>
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Go to login</Link>
          </div>
        ) : (
          <>
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6 flex items-start gap-3">
              <Users className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                <b>{invite.invited_by}</b> invited you to join <b>{invite.workspace_name}</b> as a <b>{invite.role}</b>.
                Set a password to access the shared CRM.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Email</Label>
                <Input value={invite.email} disabled className="bg-gray-100 border-gray-200 h-11 text-gray-500" data-testid="invite-email" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Your name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" className="pl-11 bg-white border-gray-300 h-11" data-testid="invite-name" required />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700 font-medium">Create a password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="At least 6 characters" minLength={6} className="pl-11 bg-white border-gray-300 h-11" data-testid="invite-password" required />
                </div>
              </div>
              <Button type="submit" className="w-full h-11 bg-[#00aeef] hover:bg-[#0090c8] text-white shadow-lg font-medium" disabled={submitting} data-testid="accept-invite-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining…</> : <>Join team <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
